import React, { useState, useEffect } from 'react';
import { campo } from './ui/campo';
import { useIdioma } from '../i18n/idioma';
import { createPortal } from 'react-dom';
import { Lead, LeadStatus } from '../types';
import { X, AlertCircle } from 'lucide-react';
import { Button } from './ui/Button';
import AddressAutocomplete from './AddressAutocomplete';
import { useSettings } from '../contexts/SettingsContext';
import { getIndustryConfig } from '../data/industryConfig';
import ServiceInterestOptions from './leads/ServiceInterestOptions';
import { validateLeadForm, LeadFormErrors } from '../services/clientValidation';

interface LeadModalProps {
  lead?: Lead;
  /** Seeds a NEW lead with what the caller already knows (e.g. the contact on a
   *  call being logged). Ignored when `lead` is supplied. */
  initialValues?: { name?: string; phone?: string; notes?: string };
  /** Overlay stacking. Defaults to z-50; pass a higher value when opening on
   *  top of another overlay. */
  overlayZClass?: string;
  /** May be async; if it rejects, the modal stays open and shows the error.
   *  Any resolved value is ignored. */
  onSave: (lead: Omit<Lead, 'id' | 'dateCreated' | 'status' | 'tags'> | Partial<Lead>) => void | Promise<unknown>;
  onClose: () => void;
}

const LeadModal: React.FC<LeadModalProps> = ({ lead, initialValues, overlayZClass = 'z-50', onSave, onClose }) => {
    const { t } = useIdioma();
  const { settings } = useSettings();
  const industryConfig = getIndustryConfig(settings.profile.industryType);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    serviceInterest: '',
    notes: '',
    value: ''
  });
  const [errors, setErrors] = useState<LeadFormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name,
        phone: lead.phone,
        email: lead.email || '',
        address: lead.address,
        serviceInterest: lead.serviceInterest,
        notes: lead.notes,
        value: lead.value ? String(lead.value) : ''
      });
      return;
    }
    if (initialValues) {
      setFormData(prev => ({
        ...prev,
        name: initialValues.name ?? prev.name,
        phone: initialValues.phone ?? prev.phone,
        notes: initialValues.notes ?? prev.notes,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead]);

  const clearError = (field: keyof LeadFormErrors) =>
    setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);

    const validationErrors = validateLeadForm(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setIsSaving(true);
    try {
      // Await before closing: the previous version fired and closed regardless,
      // so a rejected write looked exactly like a successful one.
      await onSave({
        ...formData,
        name: formData.name.trim(),
        email: formData.email.trim(),
        // formData.value holds the raw input string; Lead.value is a number.
        value: Number(formData.value) || 0,
      });
      onClose();
    } catch (err: any) {
      setSaveError(err?.message || t('prospectos.errorGuardar'));
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass = campo({ alto: 'auto' });
  const errorTextClass = 'mt-1.5 text-xs font-medium text-peligro flex items-center gap-1';
  const labelClass = 'label-ui block mb-1.5';

  return createPortal(
    <div className={`fixed inset-0 bg-navy-950/50 ${overlayZClass} flex items-end sm:items-center justify-center sm:p-4 animate-in fade-in duration-200`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-s3 w-full sm:max-w-lg overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 fade-in duration-300 h-[95dvh] sm:h-auto sm:max-h-[90dvh] flex flex-col">
        <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-acero-200 shrink-0">
          <h3 className="text-lg font-semibold tracking-tight text-navy-950">{lead ? t('leads.modal.titulo.editar') : t('leads.modal.titulo.nuevo')}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('leads.modal.aria.cerrar')}
            className="p-2 text-peligro hover:bg-peligro-bg rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className={labelClass}>{t('leads.modal.campo.nombre')}</label>
            <input
              type="text"
              autoFocus
              className={inputClass}
              value={formData.name}
              aria-invalid={!!errors.name}
              onChange={e => { setFormData({ ...formData, name: e.target.value }); clearError('name'); }}
            />
            {errors.name && <p className={errorTextClass}><AlertCircle size={13} /> {errors.name}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t('leads.modal.campo.telefono')}</label>
              <input
                type="tel"
                inputMode="tel"
                className={inputClass}
                value={formData.phone}
                aria-invalid={!!errors.phone}
                onChange={e => { setFormData({ ...formData, phone: e.target.value }); clearError('phone'); }}
              />
              {errors.phone && <p className={errorTextClass}><AlertCircle size={13} /> {errors.phone}</p>}
            </div>
            <div>
              <label className={labelClass}>{t('leads.modal.campo.email')}</label>
              <input
                type="email"
                className={inputClass}
                value={formData.email}
                aria-invalid={!!errors.email}
                onChange={e => { setFormData({ ...formData, email: e.target.value }); clearError('email'); }}
              />
              {errors.email && <p className={errorTextClass}><AlertCircle size={13} /> {errors.email}</p>}
            </div>
          </div>

          <div>
            <label className={labelClass}>{t('leads.modal.campo.direccion')}<span className="text-acero-400 font-normal">{t('leads.modal.campo.direccionopcional')}</span></label>
            <AddressAutocomplete
              value={formData.address}
              onChange={(val) => { setFormData(prev => ({ ...prev, address: val })); clearError('address'); }}
              className={`${inputClass} pl-10`}
              iconClassName="absolute left-3 top-1/2 -translate-y-1/2 text-acero-400"
              iconSize={16}
              placeholder={t('leads.modal.placeholder.direccion')}
            />
            {errors.address && <p className={errorTextClass}><AlertCircle size={13} /> {errors.address}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t('leads.modal.campo.servicio')}</label>
              <select
                className={inputClass}
                value={formData.serviceInterest}
                onChange={e => setFormData({ ...formData, serviceInterest: e.target.value })}
              >
                <option value="">{t('leads.modal.opcion.elige')}</option>
                <ServiceInterestOptions industryConfig={industryConfig} value={formData.serviceInterest} />
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('leads.modal.campo.valor')}</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                className={`${inputClass} font-data tabular-nums`}
                value={formData.value}
                onChange={e => {
                  const raw = e.target.value.replace(/[^\d.]/g, '');
                  const [whole, ...rest] = raw.split('.');
                  setFormData({ ...formData, value: rest.length ? `${whole}.${rest.join('')}` : whole });
                }}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>{t('leads.modal.campo.notas')}</label>
            <textarea
              className={`${inputClass} resize-none`}
              rows={3}
              placeholder={t('leads.modal.placeholder.notas')}
              value={formData.notes}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>

          {saveError && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-peligro-bg border border-peligro/25 text-sm font-medium text-peligro">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{saveError}</span>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end pt-4 border-t border-acero-200 gap-2 shrink-0">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isSaving} className="w-full sm:w-auto">
              {t('leads.modal.boton.cancelar')}
            </Button>
            <Button type="submit" variant="primary" isLoading={isSaving} className="w-full sm:w-auto">
              {isSaving ? t('leads.modal.boton.guardando') : lead ? t('leads.modal.boton.actualizar') : t('leads.modal.boton.crear')}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default LeadModal;